import { useEffect, useRef } from "react";
import type { RefObject } from "react";

// Not exported: nothing outside this module needs the photo shape by name,
// only the `photos` prop it shapes (fallow's dead-code check flags an
// exported type with no importer).
interface LightboxPhoto {
  src: string;
  alt: string;
}

interface LightboxProps {
  open: boolean;
  photos: readonly LightboxPhoto[];
  index: number;
  onIndexChange: (index: number) => void;
  onClose: () => void;
  /** Names the dialog for screen readers, e.g. the album title. */
  albumTitle: string;
  /** The thumbnail that opened the lightbox; focus returns there on close. */
  returnFocusTo?: RefObject<HTMLElement | null>;
}

const FOCUSABLE_SELECTOR =
  'a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])';

/**
 * Focus-trapped modal for paging through one album's photos. Mirrors
 * `MobileMenu`'s focus-trap/restore pattern. No external libraries.
 */
export function Lightbox({
  open,
  photos,
  index,
  onIndexChange,
  onClose,
  albumTitle,
  returnFocusTo,
}: LightboxProps) {
  const dialogRef = useRef<HTMLDivElement>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const openerRef = useRef<Element | null>(null);

  // Focus management: capture the opener, focus the close button, lock
  // scroll, and restore focus + scroll when the lightbox closes/unmounts.
  useEffect(() => {
    if (!open) {
      return;
    }
    openerRef.current = returnFocusTo?.current ?? document.activeElement;
    closeButtonRef.current?.focus();
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "";
      if (openerRef.current instanceof HTMLElement) {
        openerRef.current.focus();
      }
    };
  }, [open, returnFocusTo]);

  // Paging to a boundary disables the arrow button that had focus (Prev at
  // index 0, Next at the last photo). A real browser then drops focus to
  // <body>; jsdom leaves it on the now-disabled button. Either way the next
  // Tab could escape the modal, so whenever focus has left the dialog or is
  // stranded on a disabled control, pull it back to the always-enabled Close
  // button and the trap holds.
  useEffect(() => {
    if (!open) {
      return;
    }
    const dialog = dialogRef.current;
    const active = document.activeElement;
    const stranded =
      !dialog?.contains(active) ||
      (active instanceof HTMLButtonElement && active.disabled);
    if (stranded) {
      closeButtonRef.current?.focus();
    }
  }, [open, index]);

  // Keyboard: Escape closes, arrows page, Tab is trapped inside the dialog.
  useEffect(() => {
    if (!open) {
      return;
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        onClose();
        return;
      }
      if (e.key === "ArrowRight") {
        onIndexChange(Math.min(index + 1, photos.length - 1));
        return;
      }
      if (e.key === "ArrowLeft") {
        onIndexChange(Math.max(index - 1, 0));
        return;
      }
      if (e.key !== "Tab") {
        return;
      }
      const dialog = dialogRef.current;
      if (!dialog) {
        return;
      }
      const focusable = Array.from(
        dialog.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR),
      );
      if (focusable.length === 0) {
        return;
      }
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      // Focus may have escaped the dialog (e.g. a disabled arrow blurred to
      // <body>); reclaim it rather than letting Tab reach the page beneath.
      if (!dialog.contains(document.activeElement)) {
        e.preventDefault();
        first.focus();
        return;
      }
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose, index, photos.length, onIndexChange]);

  if (!open || photos.length === 0) {
    return null;
  }
  const photo = photos[index];

  return (
    <div
      ref={dialogRef}
      className="fixed inset-0 z-50 flex flex-col bg-ink/95 p-4 md:p-8"
      role="dialog"
      aria-modal="true"
      aria-label={`${albumTitle}: photo ${index + 1} of ${photos.length}`}
    >
      <div className="flex items-center justify-between text-ivory">
        <p className="text-sm text-ivory/80">
          {index + 1} / {photos.length}
        </p>
        <button
          ref={closeButtonRef}
          type="button"
          onClick={onClose}
          className="inline-flex min-h-11 min-w-11 items-center justify-center rounded-full text-2xl transition-colors hover:bg-ivory/15 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ivory"
          aria-label="Close"
        >
          ×
        </button>
      </div>
      <div className="relative flex flex-1 items-center justify-center overflow-hidden md:gap-6">
        <button
          type="button"
          onClick={() => onIndexChange(Math.max(index - 1, 0))}
          disabled={index === 0}
          className="absolute left-0 top-1/2 z-10 min-h-11 min-w-11 shrink-0 -translate-y-1/2 rounded-full bg-ink/45 text-3xl text-ivory transition-colors hover:bg-ink/60 disabled:opacity-30 md:static md:translate-y-0 md:bg-transparent"
          aria-label="Previous photo"
        >
          ‹
        </button>
        <figure className="flex h-full min-w-0 flex-col items-center justify-center">
          <img
            src={photo.src}
            alt={photo.alt}
            className="max-h-[70vh] max-w-full object-contain"
          />
          <figcaption className="mt-3 max-w-full text-center text-sm text-ivory/80">
            {photo.alt}
          </figcaption>
        </figure>
        <button
          type="button"
          onClick={() => onIndexChange(Math.min(index + 1, photos.length - 1))}
          disabled={index === photos.length - 1}
          className="absolute right-0 top-1/2 z-10 min-h-11 min-w-11 shrink-0 -translate-y-1/2 rounded-full bg-ink/45 text-3xl text-ivory transition-colors hover:bg-ink/60 disabled:opacity-30 md:static md:translate-y-0 md:bg-transparent"
          aria-label="Next photo"
        >
          ›
        </button>
      </div>
    </div>
  );
}
